/**
 * The Verification page's grouped finding: the "no basis in the protocol" questions of ONE form shown as one row, each question still
 * declinable on its own (the server puts the list in the item's `data.fields`, with the identity and fingerprint a single-question row would
 * have had; see functions/src/verification/crfReviewChecks.ts). Pure; no React.
 */
export interface GroupField {
  subject: string;
  fingerprint: string;
  field: string;
  lowered?: boolean;
}

export interface GroupView {
  /** Questions still counted in the group. */
  live: GroupField[];
  /** Questions declined on their own (live: read from the dismissals now, not from the last run). */
  declined: GroupField[];
}

function parseList(raw: string | undefined): GroupField[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is GroupField => !!x && typeof x === 'object' && typeof (x as GroupField).subject === 'string' && typeof (x as GroupField).fingerprint === 'string' && typeof (x as GroupField).field === 'string');
  } catch {
    return [];
  }
}

/**
 * Split a group's questions into live and declined, from the item's data and the dismissals as they are NOW (so a decline shows at once,
 * before the next run). A decline counts only while its fingerprint still matches.
 */
export function groupView(data: Record<string, string> | undefined, checkId: string, dismissals: Map<string, { fingerprint: string }>): GroupView | null {
  const fields = parseList(data?.fields);
  if (fields.length === 0 && !data?.declined) return null;
  const all = [...fields, ...parseList(data?.declined)];
  const isDeclined = (f: GroupField) => dismissals.get(`${checkId}:${f.subject}`)?.fingerprint === f.fingerprint;
  return { live: all.filter((f) => !isDeclined(f)), declined: all.filter(isDeclined) };
}

/** The reason recorded when a single question is declined from a group. */
export const FIELD_DECLINE_REASON = 'Declined from a grouped list of questions.';

/** One plain line for the CRF Builder's panel: "15 questions have no basis in the protocol: A, B, C, and 12 more". */
export function noBasisLine(fieldNames: string[], max = 8): string {
  const n = fieldNames.length;
  const shown = fieldNames.slice(0, max).join(', ');
  const more = n > max ? `, and ${n - max} more` : '';
  return `${n} ${n === 1 ? 'question has' : 'questions have'} no basis in the protocol: ${shown}${more}`;
}
